import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Crumb } from '../../../components/Breadcrumbs';
import limitFilter from '../../../filters/limit-filter';
import useCurriculum from '../../../hooks/useCurriculum';
import { getCurriculumById, GetCurriculumResponse } from '../api/getIndividualCurriculum';
import { getVolume } from '../api/getVolume';
import { curriculumConfig } from '../models/curriculumConfig';
import { Volume } from '../models/volume';

type Params = {
  curriculumId: string;
  volumeId: string;
};

type Status = {
  isLoading: boolean;
  isError: boolean;
};

export const useManageCurriculumVolume = () => {
  const { brand, ageCategory } = useCurriculum();
  const { curriculumId, volumeId } = useParams<Params>();
  const [crumbs, setCrumbs] = useState<Crumb[]>([
    { name: 'Organization', route: '#/org' },
    { name: 'Manage Curriculum', route: '#/manage-curriculum' },
  ]);
  const [status, setStatus] = useState<Status>({
    isLoading: true,
    isError: false,
  });
  const [curriculum, setCurriculum] = useState<GetCurriculumResponse | undefined>(undefined);
  const [volume, setVolume] = useState<Volume | undefined>(undefined);

  useEffect(() => {
    const promises: [Promise<GetCurriculumResponse>, Promise<Volume | undefined>] = [
      getCurriculumById(curriculumId),
      volumeId ? getVolume(volumeId) : Promise.resolve(undefined),
    ];

    Promise.all(promises)
      .then(values => {
        const curriculum = values[0];
        const volume = values[1];

        if (volume) {
          setCrumbs([
            ...crumbs,
            {
              name: `${curriculum.brand}: ${curriculum.name}`,
              route: curriculumConfig.timelineEnabledCurriculum.includes(brand.code)
                ? `#/manage-curriculum/${brand.code}/${ageCategory}/${curriculumId}/session-calendar`
                : `#/manage-curriculum/${brand.code}/${ageCategory}/${curriculumId}`,
            },
            { name: limitFilter(volume.name, 48) },
          ]);

          setStatus({ isLoading: false, isError: false });
          setCurriculum(curriculum);
          setVolume(volume);
        } else {
          setCrumbs([
            ...crumbs,
            {
              name: `${curriculum.brand}: ${curriculum.name}`,
              route: curriculumConfig.timelineEnabledCurriculum.includes(brand.code)
                ? `#/manage-curriculum/${brand.code}/${ageCategory}/${curriculumId}/session-calendar`
                : `#/manage-curriculum/${brand.code}/${ageCategory}/${curriculumId}`,
            },
            { name: 'New Curriculum Volume' },
          ]);
          setStatus({ isLoading: false, isError: false });
          setCurriculum(curriculum);
        }
      })
      .catch(reason => {
        console.error(reason);
        setStatus({ isLoading: false, isError: true });
      });
  }, []);

  return {
    isLoading: status.isLoading,
    isError: status.isError,
    crumbs,
    curriculum,
    volume,
  };
};
